import { useState, useMemo } from 'react';
import { FilterMatchMode } from 'primereact/api';
import { useGetClaimHistoryReportQuery } from '../../../../api/endpoints/claimHistoryReport';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { matchModeToOperator } from '../../../../utils/filterUtils';
import { formatDateForApi } from '../../../../utils/dateUtils';

const INITIAL_FILTER_STATE = {
    warrClaimsNo: null,
    warrClaimsNoMatchMode: FilterMatchMode.CONTAINS,
    islem: null,
    islemMatchMode: FilterMatchMode.CONTAINS,
    islemYapan: null,
    islemYapanMatchMode: FilterMatchMode.CONTAINS,
    bayiAdi: null,
    bayiAdiMatchMode: FilterMatchMode.CONTAINS,
    bayiKodu: null,
    bayiKoduMatchMode: FilterMatchMode.CONTAINS,
    islemTarihi: null,
    islemTarihiMatchMode: FilterMatchMode.DATE_IS,
    gunFarki: null,
    gunFarkiMatchMode: FilterMatchMode.EQUALS
};

// ⚠️ Kolon adları entity property adlarıyla BİREBİR eşleşmeli (PascalCase)
const backendColumnMap = {
    warrClaimsNo: 'WarrClaimsNo',
    islem: 'Islem',
    islemYapan: 'IslemYapan',
    bayiAdi: 'BayiAdi',
    bayiKodu: 'BayiKodu',
    islemTarihi: 'IslemTarihi',
    gunFarki: 'GunFarki'
};

const TEXT_FIELDS = ['warrClaimsNo', 'islemYapan', 'bayiAdi', 'bayiKodu', 'gunFarki'];
const DATE_FIELDS = ['islemTarihi'];
const DROPDOWN_FIELDS = ['islem'];

const buildDynamicFilters = (filtersState) => {
    const filters = [];

    TEXT_FIELDS.forEach((field) => {
        const value = filtersState[field];
        const matchMode = filtersState[`${field}MatchMode`];
        const column = backendColumnMap[field];
        if (!column || value === null || value === undefined || value === '') return;
        filters.push({ column, operator: matchModeToOperator[matchMode] ?? 1, value: String(value).trim() });
    });

    DATE_FIELDS.forEach((field) => {
        const value = filtersState[field];
        const matchMode = filtersState[`${field}MatchMode`];
        const column = backendColumnMap[field];
        const formatted = formatDateForApi(value);
        if (!column || !formatted) return;
        filters.push({ column, operator: matchModeToOperator[matchMode] ?? 5, value: formatted });
    });

    DROPDOWN_FIELDS.forEach((field) => {
        const value = filtersState[field];
        const column = backendColumnMap[field];
        if (!column || value === null || value === undefined || value === '') return;
        filters.push({ column, operator: 5, value: String(value) }); // 5 = Equals
    });

    return filters;
};

const buildDynamicSorts = (field, order) => {
    if (!field || order === 0) return null;
    const column = backendColumnMap[field];
    if (!column) return null;
    return [{ column, direction: order === 1 ? 1 : 2 }];
};

const STORAGE_KEY = 'claimHistoryReportFilters';
const loadFiltersFromStorage = () => {
    try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY));
    } catch {
        return null;
    }
};
const saveFiltersToStorage = (filters, sortField, sortOrder, lazyParams) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ filters, sortField, sortOrder, lazyParams }));
    } catch {
        console.warn('Could not save filters to storage');
    }
};

const useClaimHistoryReportData = () => {
    const { getCurrentLanguageId } = useLanguage();
    const stored = loadFiltersFromStorage();

    const [lazyParams, setLazyParams] = useState(() => stored?.lazyParams || { first: 0, rows: 20, page: 1 });
    const [sortField, setSortField] = useState(() => stored?.sortField || null);
    const [sortOrder, setSortOrder] = useState(() => stored?.sortOrder || 0);
    const [activeFilters, setActiveFilters] = useState(() => stored?.filters || INITIAL_FILTER_STATE);

    const queryParams = useMemo(() => {
        const params = { pageNumber: lazyParams.page, pageSize: lazyParams.rows };
        const filters = buildDynamicFilters(activeFilters);
        if (filters.length > 0) params.filters = filters;
        const sorts = buildDynamicSorts(sortField, sortOrder);
        if (sorts) params.sorts = sorts;
        return params;
    }, [activeFilters, lazyParams.page, lazyParams.rows, sortField, sortOrder]);

    const { data: apiData, isLoading, isFetching } = useGetClaimHistoryReportQuery(queryParams);

    const save = (f, sf, so, lp) => saveFiltersToStorage(f, sf, so, lp);

    const handleFilterChange = (updatedFilters) => {
        const lp = { first: 0, rows: lazyParams.rows, page: 1 };
        setActiveFilters(updatedFilters);
        setLazyParams(lp);
        save(updatedFilters, sortField, sortOrder, lp);
    };

    const initFilters = () => {
        const lp = { first: 0, rows: 20, page: 1 };
        setActiveFilters(INITIAL_FILTER_STATE);
        setSortField(null);
        setSortOrder(0);
        setLazyParams(lp);
        save(INITIAL_FILTER_STATE, null, 0, lp);
    };

    const onPage = (event) => {
        const lp = { first: event.first, rows: event.rows, page: event.page + 1 };
        setLazyParams(lp);
        save(activeFilters, sortField, sortOrder, lp);
    };

    const handleSortChange = (event) => {
        const sf = event?.sortField || null;
        const so = event?.sortOrder || 0;
        const lp = { first: 0, rows: lazyParams.rows, page: 1 };
        setSortField(sf);
        setSortOrder(so);
        setLazyParams(lp);
        save(activeFilters, sf, so, lp);
    };

    const exportParams = useMemo(() => {
        const params = { LanguageId: getCurrentLanguageId() }; // compressionType yok
        const filters = buildDynamicFilters(activeFilters);
        if (filters.length > 0) params.filters = filters;
        const sorts = buildDynamicSorts(sortField, sortOrder);
        if (sorts) params.sorts = sorts;
        return params;
    }, [activeFilters, sortField, sortOrder, getCurrentLanguageId]);

    return {
        apiData,
        isLoading: isLoading || isFetching,
        lazyParams,
        sortField,
        sortOrder,
        activeFilters,
        onPage,
        onSort: handleSortChange,
        handleFilterChange,
        initFilters,
        exportParams
    };
};

export default useClaimHistoryReportData;
